/**
 * CRSInfoDialog - Displays detailed CRS information
 *
 * Shows EPSG code, name, projection type, units, applicable area,
 * and accuracy details for a selected coordinate reference system.
 *
 * Validates: Requirement 44.2
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PublicIcon from '@mui/icons-material/Public';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { closeCRSInfoDialog } from '../store/slices/coordinateSystemSlice';
import { getCRSInfo } from '../services/CoordinateSystemManager';

const CRSInfoDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const { crsInfoDialogOpen, selectedCRSCode } = useAppSelector((state) => state.coordinateSystem);

  const crsInfo = selectedCRSCode ? getCRSInfo(selectedCRSCode) : null;

  const handleClose = () => {
    dispatch(closeCRSInfoDialog());
  };

  return (
    <Dialog
      open={crsInfoDialogOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="crs-info-dialog"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PublicIcon color="primary" />
          <Typography variant="h6">Coordinate Reference System</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {crsInfo ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* EPSG Code */}
            <Box>
              <Typography variant="caption" color="text.secondary">
                EPSG Code
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip label={crsInfo.epsgCode} color="primary" size="small" variant="outlined" />
              </Box>
            </Box>

            <Divider />

            {/* Name */}
            <Box>
              <Typography variant="caption" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1" fontWeight={500}>
                {crsInfo.name}
              </Typography>
            </Box>

            <Divider />

            {/* Projection Type */}
            <Box>
              <Typography variant="caption" color="text.secondary">
                Projection Type
              </Typography>
              <Typography variant="body1">{crsInfo.projectionType}</Typography>
            </Box>

            <Divider />

            {/* Units */}
            <Box>
              <Typography variant="caption" color="text.secondary">
                Units
              </Typography>
              <Typography variant="body1">{crsInfo.units}</Typography>
            </Box>

            <Divider />

            {/* Applicable Area */}
            <Box>
              <Typography variant="caption" color="text.secondary">
                Applicable Area
              </Typography>
              <Typography variant="body1">{crsInfo.applicableArea}</Typography>
            </Box>

            <Divider />

            {/* Accuracy */}
            <Box>
              <Typography variant="caption" color="text.secondary">
                Accuracy
              </Typography>
              <Typography variant="body1">{crsInfo.accuracy}</Typography>
            </Box>
          </Box>
        ) : (
          <Typography color="text.secondary">
            No CRS information available for the selected code.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CRSInfoDialog;
